import * as React from 'react';
import { Button } from 'primereact/button';
import { ButtonGroup } from 'primereact/buttongroup';
import { ReactComponent as FilterIcon } from '../../assets/icons/sliders-h.svg';
import { ReactComponent as SortIcon } from '../../assets/icons/sort-amount-up-alt.svg';
import { type MenuItem } from 'primereact/menuitem';
import { Menu } from 'primereact/menu';
import { RootState, useAppDispatch, useAppSelector } from '../../store';
import { useSelector } from 'react-redux';
import { IGroupSort, selectFilteredGroups, setCurrentPage, setSortOrder, toggleRequestStatusFilter } from '../../store/groupList';
import { Checkbox } from 'primereact/checkbox';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { Chip, ChipRemoveEvent } from 'primereact/chip';
import { UserRole } from '../../store/auth';

export default function GroupFilters() {
  const sortMenu = React.useRef();
  const filterMenu = React.useRef();

  const { filters: { currentPage, perPage, currentSortOrder, allFiltersSelected, requestStatus } } = useAppSelector((state) => state.groupList);
  const { role } = useAppSelector((state) => state.auth);

  const filteredGroups = selectFilteredGroups(useSelector((state: RootState) => state.groupList));
  const dispatch = useAppDispatch();

  function showSortMenu(e: MouseEvent): void {
    sortMenu?.current?.toggle(e);
  }

  function showFilterMenu(e: MouseEvent): void {
    filterMenu?.current?.toggle(e);
  }

  function removeStatusFilter(value: string) {
    dispatch(toggleRequestStatusFilter({ value, role }));
  }

  function generateMenuItemAttributes(order: IGroupSort) {
    function setIconDisplay(order: IGroupSort): string {
      return currentSortOrder !== order ? 'opacity-0' : '';
    }

    function setItemClass(order: IGroupSort) {
      return currentSortOrder === order ? 'bg-faded-blue' : '';
    }

    return {
      icon: `pi pi-check ${setIconDisplay(order)}`,
      className: setItemClass(order),
      command: () => dispatch(setSortOrder(order)),
    }
  }

  const sortOptions: MenuItem[] = [
    {
      label: 'Sort by',
      items: [
        {
          label: 'Group name',
          ...generateMenuItemAttributes(IGroupSort.NAME_DESC),
        },
      ]
    },
    // {
    //   label: 'Effective date',
    //   items: [
    //     {
    //       label: 'New to old',
    //       ...generateMenuItemAttributes(IGroupSort.EFFECTIVE_DATE_DESC),
    //     },
    //     {
    //       label: 'Old to new',
    //       ...generateMenuItemAttributes(IGroupSort.EFFECTIVE_DATE_ASC),
    //     }
    //   ]
    // },
    {
      label: 'Requested date',
      items: [
        {
          label: 'New to old',
          ...generateMenuItemAttributes(IGroupSort.REQUESTED_DATE_DESC),
        },
        {
          label: 'Old to new',
          ...generateMenuItemAttributes(IGroupSort.REQUESTED_DATE_ASC),
        }
      ]
    },
    {
      label: 'Reissue month',
      items: [
        {
          label: 'New to old',
          ...generateMenuItemAttributes(IGroupSort.REISSUE_MONTH_ASC),
        },
        {
          label: 'Old to new',
          ...generateMenuItemAttributes(IGroupSort.REISSUE_MONTH_DESC),
        },
      ]
    },
  ];

  function filterMenuItem({ label, value }: { label: string, value: string }) {
    return {
      label: (
        <div className="flex items-center gap-2">
          <Checkbox checked={value === 'all' ? allFiltersSelected : requestStatus.includes(value)} />
          <span>{label}</span>
        </div>
      ),
      textLabel: label,
      value,
      command: () => dispatch(toggleRequestStatusFilter({ value, role })),
    }
  }

  const filterOptions: unknown[] = [
    filterMenuItem({ label: allFiltersSelected ? 'Deselect all' : 'Select all', value: 'all' }),
    ...role === UserRole.ACTUARY ? [
      filterMenuItem({ label: 'Assigned to me', value: 'assigned_to_me' }),
      filterMenuItem({ label: 'Unassigned', value: 'unassigned' })
    ] : [],
    filterMenuItem({ label: 'Requested by AM', value: 'requested_by_am' }),
    filterMenuItem({ label: 'Approved by Actuarial', value: 'approved_by_actuarial' }),
    filterMenuItem({ label: 'Declined by Actuarial', value: 'declined_by_actuarial' }),
  ];

  return (
    <>
      <section className="flex justify-between gap-4">
        <ButtonGroup>
          <Button label="filter" severity="secondary" rounded icon={<FilterIcon className="w-3 h-3" />} className="rounded-r-none" onClick={showFilterMenu} badge={requestStatus.length ? requestStatus.length : null} badgeClassName="bg-white border-grey-4 text-allstate-blue" />
          <Button label="sort" severity="secondary" rounded icon={<SortIcon className="w-3 h-3" />} className="-ml-1 rounded-l-none" onClick={showSortMenu} />
        </ButtonGroup>

        {filteredGroups.length > perPage && (
          <Paginator
            rows={perPage}
            totalRecords={filteredGroups.length}
            first={perPage * (currentPage - 1)}
            onPageChange={(e: PaginatorPageChangeEvent) => dispatch(setCurrentPage(e.page + 1))}
            template="PrevPageLink NextPageLink"
          />
        )}
      </section>

      {requestStatus.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {requestStatus.map((status, statusIndex) => {
            const matchingFilterItem = Object.values(filterOptions).find((option) => option.value === status);

            if (matchingFilterItem) {
              return (
                <Chip
                  key={statusIndex}
                  label={matchingFilterItem.textLabel!}
                  removable
                  onRemove={(e: ChipRemoveEvent) => removeStatusFilter(matchingFilterItem.value) }
                />
              )
            }

            return null;
          })}
        </div>
      )}
      
      <Menu model={sortOptions} ref={sortMenu} popup={true} expanded={true} />
      <Menu model={filterOptions} ref={filterMenu} popup />
    </>
  )


}